import { toValue, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { yardAssetKeys } from '@/composables/queryKeys';
import assetVisitService from '@satellite/services/asset-visit-service';

import type { MaybeRefOrGetter } from 'vue';
import type { UseQueryOptions } from '@tanstack/vue-query';

type AssetId = string | null | undefined;
type WarehouseId = string | null | undefined;

export function useAssetById<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
  assetId: MaybeRefOrGetter<AssetId>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> & {
    enabled?: MaybeRefOrGetter<boolean>;
  }
) {
  return useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey: computed(() => ['yard-asset', toValue(assetId)] as const),
    enabled: () => {
      const id = toValue(assetId);
      const externalEnabled = toValue(
        (options?.enabled as MaybeRefOrGetter<boolean> | undefined) ?? true
      );
      return externalEnabled && Boolean(id);
    },
    queryFn: () => {
      const id = toValue(assetId);
      if (!id) throw new Error('Missing assetId');
      return assetVisitService.getAssetContainerById(id) as Promise<TQueryFnData>;
    }
  });
}

export function useAssetContainersByWarehouseId<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData
>(
  warehouseId: MaybeRefOrGetter<WarehouseId>,
  params?: MaybeRefOrGetter<Record<string, unknown>>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> & {
    enabled?: MaybeRefOrGetter<boolean>;
  }
) {
  return useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey: computed(() =>
      yardAssetKeys.assetContainers.byWarehouse(toValue(warehouseId), toValue(params))
    ),
    enabled: () => {
      const id = toValue(warehouseId);
      const externalEnabled = toValue(
        (options?.enabled as MaybeRefOrGetter<boolean> | undefined) ?? true
      );
      return externalEnabled && Boolean(id);
    },
    queryFn: async () => {
      const id = toValue(warehouseId);
      const queryParams = toValue(params) || {};
      if (!id) throw new Error('Missing warehouseId');
      return assetVisitService.getAssetContainersByWarehouseId(
        id,
        queryParams
      ) as Promise<TQueryFnData>;
    }
  });
}
